import { PlayerAPI } from 'bitmovin-player';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';

// Constants
import { STORE_NAME } from '@intigral/constant';

// Shared Modules
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { getBaseSubscription } from '@intigral/utils/lib/user';
import { toast } from '@intigral/shared';

// Helpers
import f2 from '../../../helpers/formatter2';
import channelsUtil from '../../../helpers/channelsUtil';
import playerService from '../../../helpers/bitmovinPlayer';
import { isEpgProgramParentalBlocked } from '../utils/parentalControl';
import { getEpgPlaybackBlockReason } from '../utils/playback';
import { usePPV } from './ppv/usePPV';

// Redux Actions
import { showUpgradedModal } from '../../../store/reducers/generalConfigurationSlice';

// Translations
import { __ } from '../../../translations';

import { useChannelFavorites } from '../../../hooks/useChannelFavorites';
import useChannelList from '../../../hooks/useChannelList';

export const useEpgHandler = (props) => {
    const isChannelRef = useRef(false);
    const isProgramRef = useRef(false);

    const {
        favorites,
        userChannels,
        freeTierGuid,
        channelImageId,
        DEFAULT_GENRE_SELECTED,
        programs,
        channels,
        ppvEventConfig,
        operators,
        selectedProgram,
        isStartOver,
        selectedDate,
        isFutureProgram,
        streamingUrl,
        setSelectedCategory,
        setListedChannels,
        setSelectedChannel,
        setSelectedDate,
        setStreamingUrl,
        setIsStartOver,
        setIsFutureProgram,
        setIsRewindProgram,
        setSelectedProgram,
        setIsShowEpgLock,
        setIsSwitch,
    } = props;

    const userInfo = get(STORE_NAME.USER);
    const { addFavorite, removeFavorite } = useChannelFavorites();
    const { updateChannelData } = useChannelList();
    const { getPPVEventForChannel, isPPVEventPurchased, enhanceChannelWithPPV, handlePPVPurchase } = usePPV({
        ppvEventConfig,
        operators,
    });
    const dispatch = useDispatch();

    const STREAM_URL_DELAY_MS = 200;

    const scheduleStreamPlayback = (program, streamUrl: string | null, isLockedProgram: boolean) => {
        const blockReason = getEpgPlaybackBlockReason(program, isLockedProgram);

        if (blockReason === 'subscription') {
            setIsShowEpgLock(true);
            return;
        }

        if (blockReason === 'parental' || !streamUrl) {
            return;
        }

        setTimeout(() => setStreamingUrl(streamUrl), STREAM_URL_DELAY_MS);
    };

    /**
     * Handles category selection and filters channels accordingly.
     *
     * @param {string} value - The selected category value.
     */
    const onCategoryChangeHandler = (value: string) => {
        // Update the selected category state
        setSelectedCategory(value);

        let filtered = [];

        // If 'Favorite' is selected, filter channels that are in the favorites list
        if (value === 'Favorite') {
            filtered = userChannels.channels.filter((channel) => favorites.includes(channel.channelID));
        }
        // If a specific genre/category is selected (other than default), filter by category
        else if (value !== DEFAULT_GENRE_SELECTED) {
            filtered = userChannels.channels.filter((channel) =>
                channel.category?.some((cat) => cat.toLowerCase() === value.toLowerCase())
            );
        }
        // If default genre is selected, show all channels
        else {
            filtered = userChannels.channels;
        }

        // Update the listed channels state
        setListedChannels(filtered);

        // If there are filtered channels, format the first one and set as selected
        if (filtered.length > 0) {
            const formattedChannel = f2.formattedChannelData(filtered, channelImageId);
            const filteredEpg = channelsUtil.getEpgFilteredChannels(formattedChannel, selectedDate);
            const firstChannel = filteredEpg?.[0] ?? null;
            setSelectedChannel(firstChannel ? { ...firstChannel, hideUpgradeBanner: true } : null);
        }
    };

    /**
     * Handles the change of date selection.
     *
     * @param {string} date - The newly selected date in string format.
     */
    // const onDateChangeHandler = (date: string) => {
    //     // Update the selected date state
    //     setSelectedDate(date);
    // };

    /**
     * Handles the click event on a locked item.
     * Determines the user's subscription tier and shows appropriate feedback or upgrade modal.
     */
    const onLockClickHandler = (isOutsideRewindBuffer?: boolean) => {
        // Check if the current selected channel is a PPV channel
        // If it is, we should not show the generic addon subscription toast
        const currentSelectedChannel = channelsUtil.getSelectedChannel();
        const isCurrentChannelPPV = (currentSelectedChannel as any)?.isPPV;

        // Skip showing toast/modal for PPV channels - they have their own handling
        if (isCurrentChannelPPV) {
            return;
        }
        if (isOutsideRewindBuffer) {
            dispatch(showUpgradedModal(true));
            return;
        }
        // Get user's current subscription tier status, fallback to base subscription if not available
        const tierStatus = userInfo?.tierStatus || getBaseSubscription(userInfo);

        // If user has an active subscription and it's not the free tier, show a subscription-related error toast
        if (tierStatus?.active && tierStatus?.tierGuid !== freeTierGuid) {
            toast(__('addons_subscription_message'), 'error');
        }
        // Otherwise, prompt the user with the upgrade modal
        else {
            dispatch(showUpgradedModal(true));
        }
    };

    /**
     * Handles the click event to toggle a channel as a favorite.
     *
     * @param {string} channelId - The ID of the channel to be added or removed from favorites.
     */

    const onFavoriteClickHandler = async (channelId: string) => {
        try {
            // Validate that the channelId is not null or undefined
            if (!channelId) {
                return; // Exit early if channelId is invalid
            }

            const updatedFavoriteChannels = null;

            // Toggle the favorite status: remove if it's already a favorite, otherwise add it

            if (favorites.includes(channelId)) {
                removeFavorite.mutate(channelId);
            } else {
                addFavorite.mutate(channelId);
            }

            // If the update was successful, refresh the channel data
            if (updatedFavoriteChannels?.payload) {
                updateChannelData(userChannels?.channels ?? [], updatedFavoriteChannels?.payload ?? null);
            }
        } catch (error) {
            void error;
        }
    };

    /**
     * Starts or restarts the playback based on the selected program's timing.
     *
     * If the selected program is in the past, it will seek to the beginning of the stream.
     * If the selected program is currently live, it will set up the start-over URL for playback.
     */
    const startOverPlayback = () => {
        // Get the current player instance from the player service
        const player = playerService.getPlayer();

        // Check if the player exists and the selected program is in the past
        if (player && channelsUtil.isEarlier(selectedProgram)) {
            // If the program is earlier, reset the playback to the start (seek to 0)
            player.seek(0);
            return;
        }
        // Check if the player exists and the selected program is currently playing
        else if (player && channelsUtil.isNow(selectedProgram)) {
            if (isEpgProgramParentalBlocked(selectedProgram)) {
                setStreamingUrl(null);
                return;
            }

            // Stop the current playback and prepare for the start-over feature
            setStreamingUrl(null);
            setIsStartOver(true);

            // Construct the start-over URL by appending start time
            const streamUrl = channelsUtil.appendStartEndTime(
                selectedProgram?.channel.tuningURLs.startOverURL,
                selectedProgram.startTime
            );

            // Delay the setting of the streaming URL to give time for any transitions
            setTimeout(() => {
                setStreamingUrl(streamUrl); // Set the new stream URL
            }, 200); // Delay for 200ms to ensure smooth transition
        }
    };

    /**
     * Resets the state variables related to playback and program control.
     *
     * This function clears the current streaming URL and resets flags that control
     * the playback behavior, such as whether the program is a future program,
     * a rewind program, or a start-over program.
     */
    const resetState = () => {
        // Clear the current streaming URL
        setStreamingUrl(null);

        // Reset playback-related flags: future program, rewind, and start-over states
        setIsFutureProgram(false);
        setIsRewindProgram(false);
        setIsStartOver(false);
        setIsShowEpgLock(false);
    };

    /**
     * Handles user selection of a program.
     *
     * Based on the program's timing (past, present, future), it sets the appropriate
     * playback flags and streaming URL.
     *
     * @param {any} program - The selected program object.
     */
    const onProgramSelectHandler = (program, channel) => {
        // Prevent double click and add some delay to avoid multiple rapid selections and stream error
        if (isProgramRef.current) return;

        isProgramRef.current = true;
        if (program) {
            // Enhanced PPV handling
            let enhancedProgram = { ...program };

            // Check if this is a PPV channel
            if (channel?.isPPV) {
                const ppvEvent = getPPVEventForChannel(channel);
                const isPPVPurchased = isPPVEventPurchased(ppvEvent);

                // Enhance the program with PPV information
                enhancedProgram = {
                    ...program,
                    isPPV: true,
                    ppvEvent: ppvEvent,
                    isPPVPurchased: isPPVPurchased,
                    ppvStatus: isPPVPurchased ? 'Subscribed' : 'Not Subscribed',
                    ppvEventId: ppvEvent?.eventId,
                    ppvEventName: ppvEvent?.name,
                    ppvPrice: ppvEvent?.price,
                    ppvCurrency: ppvEvent?.currency,
                };
            }

            const isLockedProgram = Boolean(
                program?.channel?.locked || program?.channel?.isLockedEpg || program?.locked
            );
            if (!enhancedProgram?.isPPV && isLockedProgram && !program?.hideUpgradeBanner) {
                if (onLockClickHandler) onLockClickHandler(program?.locked);
            } else {
                // check if streamUrl is already set or isFutureProgram is true then isSwitch = true
                const isPreviousStreamUrl = streamingUrl !== null || isFutureProgram ? true : false;
                // Reset playback-related states
                resetState();

                // Update the selected program state with enhanced PPV data
                setSelectedProgram(enhancedProgram);
                setSelectedChannel(channel);

                let streamUrl = null;

                if (channelsUtil.isNow(program)) {
                    // If the program is currently live, use the live URL
                    streamUrl = program?.channel?.tuningURLs?.liveURL;
                } else if (channelsUtil.isEarlier(program)) {
                    // If the program has already aired, mark it as a rewind program
                    setIsRewindProgram(true);

                    // Build the catch-up stream URL using start and end time
                    streamUrl = channelsUtil.appendStartEndTime(
                        program?.channel?.tuningURLs?.catchUpURL,
                        program?.startTime,
                        program?.endTime
                    );
                } else {
                    // If the program is scheduled for the future, set future flag
                    setIsFutureProgram(true);
                }
                if (isPreviousStreamUrl) {
                    setIsSwitch(true);
                } else {
                    setIsSwitch(false);
                }
                scheduleStreamPlayback(program, streamUrl, isLockedProgram);
            }
        }
        setTimeout(() => {
            isProgramRef.current = false;
        }, 3000); // after 3 seconds user can select another program to avaoid stream error due to multiple rapid selections
    };

    const clearChannelCategoryFilter = () => {
        setSelectedCategory(DEFAULT_GENRE_SELECTED);
        setListedChannels(null);
    };

    const onChannelSearchSelectHandler = (selectedChannel) => {
        clearChannelCategoryFilter();
        onChannelSelectHandler(selectedChannel);
    };

    /**
     * Handles user selection of a channel.
     *
     * It determines the current (live) program of the selected channel and prepares
     * the player for playback.
     *
     * @param {any} selectedChannel - The selected channel object.
     */
    const onChannelSelectHandler = (selectedChannel) => {
        // Prevent double click and add some delay to avoid multiple rapid selections and stream error
        if (isChannelRef.current) return;

        isChannelRef.current = true;

        // Enhanced PPV handling for the channel using the helper function
        const enhancedChannel = enhanceChannelWithPPV(selectedChannel);

        // Check if content is purchased by comparing channel subscriptionGUID with user's PPV list
        const userInfo = get(STORE_NAME.USER);
        const userPpvList = userInfo?.ppvList || [];
        const subscriptionGUID = enhancedChannel?.subscriptions?.[0]?.subscriptionGUID;
        const isPurchased = subscriptionGUID && userPpvList.some((ppv) => ppv.guid === subscriptionGUID);

        // For PPV channels, we should never show the lock modal
        // PPV channels should always be allowed to open, and PPV-specific logic
        // should be handled in the program selection, not channel selection
        const isLockedChannel = enhancedChannel?.isPPV
            ? false // PPV channels are never "locked" in the traditional sense
            : Boolean(enhancedChannel?.locked || enhancedChannel?.isLockedEpg);

        if (isLockedChannel && !enhancedChannel?.hideUpgradeBanner) {
            if (onLockClickHandler) onLockClickHandler();
        } else {
            // set selected channel to the state
            channelsUtil.setSelectedChannel({ ...enhancedChannel });

            // Get all programs for the selected channel
            const selectedChannelPrograms = programs?.filter((program) => program?.channelUuid === enhancedChannel?.id);

            // Determine the index of the currently airing program
            const index = channelsUtil.getCurrentProgramIndex(selectedChannelPrograms);
            const currentProgram = selectedChannelPrograms?.[index];
            // Set the current program as selected with enhanced channel
            if (currentProgram) {
                onProgramSelectHandler(currentProgram ?? null, enhancedChannel);
            }
        }
        setTimeout(() => {
            isChannelRef.current = false;
        }, 3000); // after 3 seconds user can select another program to avaoid stream error due to multiple rapid selections
    };

    /**
     * Handles the "Go Live" action.
     *
     * This function identifies the current channel from the selected program and triggers
     * the channel selection logic to switch to the live stream.
     */
    const onGoLiveHandler = () => {
        // If no program is selected, exit early
        if (!selectedProgram) return;
        const todayDate = channelsUtil.getFormattedTime(Date.now(), 'YYYY-MM-DD');
        if (selectedDate === todayDate) {
            // Extract the channel UUID from the selected program
            const currentChannelId = selectedProgram.channelUuid;

            // Find the corresponding channel object from the list of channels
            const channel = channels?.find((c) => c.uuid === currentChannelId);

            // Trigger the handler to load the live stream of the selected channel
            onChannelSelectHandler(channel);
        } else {
            setSelectedDate(todayDate);
            resetState();
        }
    };

    /**
     * Handles the action after a rewinded program completes.
     *
     * Finds the next program on the same channel and triggers its selection
     * for playback or further interaction.
     */
    const onRewindCompleted = () => {
        if (!selectedProgram) return;

        // Get all programs for the currently selected channel
        const currentSelectedChannelPrograms = programs?.filter(
            (program) => program?.channelUuid === selectedProgram?.channelUuid
        );

        // Find the index of the currently selected program
        const currentProgramIndex =
            currentSelectedChannelPrograms?.findIndex(
                (program) => program?.id === selectedProgram?.id && program?.endTime === selectedProgram?.endTime
            ) ?? 0;

        // Get the next program from the list, if available
        const nextProgram = currentSelectedChannelPrograms?.[currentProgramIndex + 1];

        // Trigger selection of the next program
        onProgramSelectHandler(nextProgram, selectedProgram?.channel);
    };

    /**
     * Callback function triggered when the player is ready.
     *
     * If the start-over flag is active, this function performs a time shift
     * to the beginning of the available stream (i.e., maximum rewind).
     *
     * @param {any} player - The video player instance.
     */
    const playerReadyCallback = (player: PlayerAPI) => {
        // Ensure the player is available and start-over mode is enabled
        if (player && isStartOver) {
            // Perform time shift to the earliest point in the buffer
            player.timeShift(player.getMaxTimeShift());
        } else if (player && !isStartOver) {
            player.timeShift(-5);
        }
    };

    return {
        onCategoryChangeHandler,
        // onDateChangeHandler,
        onLockClickHandler,
        onFavoriteClickHandler,
        startOverPlayback,
        resetState,
        onProgramSelectHandler,
        onChannelSelectHandler,
        onChannelSearchSelectHandler,
        onGoLiveHandler,
        onRewindCompleted,
        playerReadyCallback,
        handlePPVPurchase,
    };
};
